import React, { ReactElement, RefObject, useRef } from "react";
import { useDependency } from "../../../../contexts/DependencyContext";
import { useTimeMachine } from "../../../../contexts/TimeMachineContext";
import { useRecurrentAsyncCall } from "../../../../hooks/UseAsyncCall";
import { DataSourceService } from "../../../../services/http/datasource/DataSourceService";
import { IDataSource } from "../../../../services/http/datasource/models/IDataSource";
import { INumericTimeSeriesPoints } from "../../../../services/http/measure/models/INumericTimeSeriesPoints";
import { MeasureService } from "../../../../services/http/measure/MeasureService";
import { GetDataSourceMeasureHistoryByPointsParameters } from "../../../../services/http/measure/models/GetDataSourceMeasureHistoryByPointsParameters";
import { DateService } from "../../../../services/types/DateService";
import IndicatorNumber from "../../IndicatorNumber";
import LineChart, { ILineChartSeries, ChartPointsPerPixelRatio } from "../../LineChart";
import Spinner from "../../Spinner";
import NoData from "../../NoData";
import styles from './SensorChart.module.scss';
import { TooltipItem } from "chart.js";

interface ISensorChartContent
{
    dataSource: IDataSource;
    timeSeriesPoints: INumericTimeSeriesPoints | null;
}

interface ISensorChartProps
{
    dataSourceId: number;
    uiConfiguration: string;
}

async function loadData(dataSourceService: DataSourceService, measureService: MeasureService, dateService: DateService, dataSourceId: number, date: Date, chartContainerRef: RefObject<HTMLDivElement>, uiConfiguration: string): Promise<ISensorChartContent>
{
    const maxPoints = chartContainerRef.current ? Math.round(chartContainerRef.current.offsetWidth * ChartPointsPerPixelRatio) : 1;
    const dataSource = await dataSourceService.getById(dataSourceId);
    const parameters = new GetDataSourceMeasureHistoryByPointsParameters();
    parameters.dataSourceId = dataSourceId;
    parameters.maxPoints = maxPoints;
    parameters.toDate = date ?? new Date();
    parameters.fromDate = dateService.addDays(parameters.toDate, -1);
    const uiConfig = JSON.parse(uiConfiguration);

    // if the time machine date exists, use the old non-cachable endpoint.
    if (date)
    {
        const timeSeriesPoints = await measureService.getNumericTimeSeriesPoints(parameters)
        return { dataSource, timeSeriesPoints };
    }
    else
    {
        if (uiConfig.visualClasses ===  "sensor-chart-widget box-item-full-width")
        {
            const timeSeriesPoints = await measureService.getNumericTimeSeriesLargePoints(dataSourceId)
            return { dataSource, timeSeriesPoints };
        }
        else{
            const timeSeriesPoints = await measureService.getNumericTimeSeriesSmallPoints(dataSourceId);
            return { dataSource, timeSeriesPoints };
        }
    }
}

const SensorChart = (props: ISensorChartProps): ReactElement =>
{
    const dataSourceService = useDependency(DataSourceService);
    const measureService = useDependency(MeasureService);
    const dateService = useDependency(DateService);
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const timeMachineDate = useTimeMachine();
    const { loading, content } = useRecurrentAsyncCall(parseInt(process.env.REACT_APP_CHART_REFRESH_RATE ?? "300000"), loadData, dataSourceService, measureService, dateService, props.dataSourceId, timeMachineDate, chartContainerRef, props.uiConfiguration);
    let dateRange: Date[] | undefined;

    const getChartSeries = (): ILineChartSeries[] =>
    {
        if (loading || !content || !content.dataSource || !content.timeSeriesPoints)
            return [];

        const { dateFrom, dateTo, values } = content.timeSeriesPoints;
        dateRange = dateService.getDateRange(dateFrom, dateTo, values.length);

        return [{
            title: content.dataSource.name,
            labels: dateRange.map(x => x.toJSON()),
            values: values.map(x => x !== null ? x : null)
        } as ILineChartSeries];
    }

    function getChartLabel(): string
    {
        const dataSource = content?.dataSource;
        return dataSource ? `${dataSource.unitOfMeasureName} ${(dataSource.unitOfMeasureId !== 41 ? `(${dataSource.unitOfMeasureCode})` : dataSource.unitOfMeasureCode)}` : '';
    }

    const getTooltipLabel = (tooltipItem: TooltipItem<"line">): string =>
    {
        const fixedRawValue = Number(tooltipItem.raw)?.toFixed(5);
        const floatValue = parseFloat(fixedRawValue);
        return floatValue.toString();
    };

    const getTooltipTitle = (tooltipItems: TooltipItem<"line">[]): string =>
    {
        if (!content?.timeSeriesPoints || !tooltipItems || tooltipItems.length === 0) return '';

        const index = Number(tooltipItems[0].dataIndex);

        if (index === null || isNaN(index) || !dateRange)
            return '';

        return dateService.format(new Date(dateRange[index]), 'MM/dd/yyyy HH:mm:ss');
    }

    const chartSeries = getChartSeries();
    const hasValidMeasures = chartSeries && chartSeries.length > 0 && chartSeries[0].values.length > 0;

    return (
        <div>
            {
                content &&
                content.dataSource &&
                content.timeSeriesPoints &&
                content.timeSeriesPoints.aggregation &&
                hasValidMeasures &&
                <div className={styles.indicators}>
                    <IndicatorNumber label="Min value" unitOfMeasure={content.dataSource.unitOfMeasureCode} value={content.timeSeriesPoints.aggregation.minimum} decimalPlaces={2} />
                    <IndicatorNumber label="Max value" unitOfMeasure={content.dataSource.unitOfMeasureCode} value={content.timeSeriesPoints.aggregation.maximum} decimalPlaces={2} />
                    <IndicatorNumber label="Average" unitOfMeasure={content.dataSource.unitOfMeasureCode} value={content.timeSeriesPoints.aggregation.average} decimalPlaces={2} />
                    <IndicatorNumber label="Standard Deviation" unitOfMeasure={content.dataSource.unitOfMeasureCode} value={content.timeSeriesPoints.aggregation.standardDeviation} decimalPlaces={2} />
                </div>
            }

            <div ref={chartContainerRef}>
                {loading
                    ? <Spinner caption="Loading data..." />
                    : hasValidMeasures
                        ? <LineChart
                            series={chartSeries}
                            yLabel={getChartLabel()}
                            getTooltipLabel={getTooltipLabel}
                            getTooltipTitle={getTooltipTitle}
                            timeUnit="minute"
                            enableZoom={false}
                            displayLegend={false}
                            size="small"
                        />
                        : <NoData badgeText="No measures" message={`No measures received at ${dateService.format(timeMachineDate ?? new Date(), 'MM/dd/yyyy HH:mm:ss')}`} />}
            </div>
        </div>);
}

export default SensorChart;